import { ObservableStore } from "./ObservableStore";
import { TypedEventHub } from "./TypedEventHub";

type CommandHandler<T extends { request: unknown; response: unknown }> = (
  request: T["request"],
) => T["response"];

/** Observable store wrapper with emitCurrent subscribe (product UI parity). */
export class ProductObservableStore<T> {
  private readonly store: ObservableStore<T>;

  constructor(value: T) {
    this.store = new ObservableStore(value);
  }

  get snapshot(): Readonly<T> {
    return this.store.value;
  }

  set(value: T): void {
    this.store.set(value);
  }

  update(update: (value: Readonly<T>) => T): void {
    this.store.update(update);
  }

  subscribe(listener: (value: Readonly<T>) => void): () => void {
    const subscription = this.store.subscribe((value) => listener(value), {
      emitCurrent: true,
    });
    return () => subscription.dispose();
  }

  dispose(): void {
    this.store.dispose();
  }
}

/**
 * Shared typed product messaging: commands + events + named stores.
 * Used by Configurator and Room Designer (and mirrors ViewerRuntimeScope shape).
 */
export class ProductMessaging<
  TCommands extends {
    [K in keyof TCommands]: { request: unknown; response: unknown };
  },
  TEvents extends object,
  TStores extends object,
> {
  private readonly commands = new Map<
    keyof TCommands,
    (request: unknown) => unknown
  >();
  private readonly events = new TypedEventHub<TEvents>();
  private readonly stores = new Map<
    keyof TStores,
    ProductObservableStore<TStores[keyof TStores]>
  >();

  constructor(initialStores: { [K in keyof TStores]: TStores[K] }) {
    for (const key of Object.keys(initialStores) as Array<keyof TStores>) {
      this.stores.set(key, new ProductObservableStore(initialStores[key]));
    }
  }

  register<K extends keyof TCommands>(
    name: K,
    handler: CommandHandler<TCommands[K]>,
  ): () => void {
    if (this.commands.has(name)) {
      throw new Error(`Product command already registered: ${String(name)}`);
    }
    const registered = handler as unknown as (request: unknown) => unknown;
    this.commands.set(name, registered);
    return () => {
      if (this.commands.get(name) === registered) this.commands.delete(name);
    };
  }

  execute<K extends keyof TCommands>(
    name: K,
    request: TCommands[K]["request"],
  ): TCommands[K]["response"] {
    const handler = this.commands.get(name);
    if (!handler) {
      throw new Error(`Product command has no owner: ${String(name)}`);
    }
    return handler(request) as TCommands[K]["response"];
  }

  on<K extends Extract<keyof TEvents, string>>(
    name: K,
    listener: (detail: TEvents[K]) => void,
  ): () => void {
    const subscription = this.events.on(name, listener);
    return () => subscription.dispose();
  }

  emit<K extends Extract<keyof TEvents, string>>(
    name: K,
    detail: TEvents[K],
  ): void {
    (
      this.events.emit as (
        eventName: Extract<keyof TEvents, string>,
        payload: TEvents[Extract<keyof TEvents, string>],
      ) => void
    )(name, detail as unknown as TEvents[Extract<keyof TEvents, string>]);
  }

  store<K extends keyof TStores>(name: K): ProductObservableStore<TStores[K]> {
    const store = this.stores.get(name);
    if (!store) throw new Error(`Unknown product store: ${String(name)}`);
    return store as unknown as ProductObservableStore<TStores[K]>;
  }

  dispose(): void {
    this.commands.clear();
    this.events.dispose();
    for (const store of this.stores.values()) store.dispose();
    this.stores.clear();
  }
}
